import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle2, Timer, PauseCircle, Package } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { StageNotifyPicker } from '@/components/production/StageNotifyPicker';
import { formatHours } from '@/components/production/DelayBadge';
import { slaConfigStageLabel } from '@/lib/slaLabels';

/**
 * Wave 3.78 / 3.82 / 3.83 / 3.85 — Edit SLA + per-stage notify recipients.
 *
 * Two operating modes:
 *  * ``mode='existing'`` (default) — Edits a live order. Loads the order's
 *    timeline (stage_history + procurement summary) so completed stages
 *    are locked and time-in-stage is shown. Saves via
 *    ``PUT /api/orders/{id}/sla-config``.
 *  * ``mode='draft'`` (Wave 3.85) — Configures SLA for an order that
 *    hasn't been created yet (New Order form). Seeds defaults from the
 *    resolved SLA template for the selected jewellery type via
 *    ``GET /api/production-timeline/sla-template/resolve``. On save it
 *    calls ``onDraftSave(payload)`` — the caller is responsible for
 *    persisting the SLA config as part of the create-order POST.
 *
 * Guardrails:
 *  * Stages that have already completed (status === 'completed') are frozen
 *    (both the SLA input and the notify picker are disabled). This preserves
 *    the historical delay classification. In draft mode no stage is ever
 *    completed so nothing is locked.
 *  * Fulfilling Requirements is replaced by FOUR parallel material tracks
 *    (Gold / Diamond / Colour Stone / Pearl) whose SLAs and notify lists
 *    are stored on ``order.procurement_config``.
 *  * SLA tracking scope (timeline / procurement) can be toggled off per-order.
 *  * The backend guards the "Completed / Cancelled" order-lifecycle case
 *    and role-based auth; this dialog does not attempt to duplicate those.
 */

const PROCUREMENT_MATERIALS = [
  { kind: 'gold',        label: 'Gold Requirement',         defaultSla: 3, hint: 'Starts on CAM confirm or first gold requirement' },
  { kind: 'diamond',     label: 'Diamonds Requirement',     defaultSla: 4, hint: 'Starts on first diamond requirement' },
  { kind: 'color_stone', label: 'Color Stones Requirement', defaultSla: 4, hint: 'Starts on first colour stone requirement' },
  { kind: 'pearl',       label: 'Pearls Requirement',       defaultSla: 3, hint: 'Starts on first pearl requirement' },
];

const defaultDraftProcurementConfig = (procDefaults = []) => {
  const bySla = Object.fromEntries((procDefaults || []).map((p) => [p.kind, Number(p.sla_days) || 0]));
  const out = {};
  for (const m of PROCUREMENT_MATERIALS) {
    out[m.kind] = { sla_days: bySla[m.kind] != null ? bySla[m.kind] : m.defaultSla, notify_user_ids: [] };
  }
  return out;
};

export function EditOrderSlaDialog({
  open,
  onOpenChange,
  order,
  companyId,
  onSaved,
  canToggleTracking = false,
  // Wave 3.85 — Draft mode props (used by New Order form).
  mode = 'existing',
  jewelleryType = '',
  draftValues = null,
  onDraftSave = null,
}) {
  const isDraft = mode === 'draft';

  const [timeline, setTimeline] = useState(null);
  const [template, setTemplate] = useState({ stages: [], procurement_defaults: [] });
  const [notifyUsers, setNotifyUsers] = useState([]);
  const [slaOverrides, setSlaOverrides] = useState({});
  const [notifyByStage, setNotifyByStage] = useState({});
  const [delayNotifyUserIds, setDelayNotifyUserIds] = useState([]);
  // Wave 3.99ah — Global "Order Raised" notify list (notify-only).
  const [orderRaisedNotify, setOrderRaisedNotify] = useState([]);
  const [procurementConfig, setProcurementConfig] = useState({});
  const [slaScope, setSlaScope] = useState({ timeline: true, procurement: true });
  const [initialSlaScope, setInitialSlaScope] = useState({ timeline: true, procurement: true });
  const [priority, setPriority] = useState('normal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const orderId = order?.id;

  useEffect(() => {
    if (!open) return;
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        if (isDraft) {
          // Wave 3.85 — Draft mode: fetch the RESOLVED template for the
          // selected jewellery type (base + per-type override) plus the
          // resolved procurement defaults. No timeline data — no order exists.
          const [tpl, us] = await Promise.all([
            api.get('/production-timeline/sla-template/resolve', {
              params: { company_id: companyId, jewellery_type: jewelleryType || '' },
            }),
            api.get('/production-timeline/users', { params: { company_id: companyId } }),
          ]);
          if (cancelled) return;
          const resolved = tpl.data || { stages: [], procurement_defaults: [] };
          setTemplate({
            stages: resolved.stages || [],
            procurement_defaults: resolved.procurement_defaults || [],
          });
          setNotifyUsers(us.data || []);
          setTimeline(null); // no history in draft mode

          // Seed initial state from draftValues where present, otherwise from resolved defaults.
          const dv = draftValues || {};
          setSlaOverrides({ ...(dv.sla_per_stage || {}) });
          setNotifyByStage({ ...(dv.delay_notify_by_stage || {}) });
          setDelayNotifyUserIds([...(dv.delay_notify_user_ids || [])]);
          // Order Raised: caller's list wins; else seed from resolved template.
          setOrderRaisedNotify([
            ...(dv.order_raised_notify_user_ids
              || resolved.order_raised_notify_user_ids
              || []),
          ]);

          // Merge caller's procurement_config with resolved template defaults —
          // template supplies fresh sla_days; caller's notify_user_ids win.
          const seededProc = defaultDraftProcurementConfig(resolved.procurement_defaults);
          const merged = { ...seededProc };
          for (const kind of Object.keys(dv.procurement_config || {})) {
            const cur = dv.procurement_config[kind] || {};
            merged[kind] = {
              sla_days: cur.sla_days != null ? Number(cur.sla_days) : seededProc[kind]?.sla_days || 0,
              notify_user_ids: cur.notify_user_ids || [],
            };
          }
          setProcurementConfig(merged);

          const scope = {
            timeline: dv?.sla_scope?.timeline !== false,
            procurement: dv?.sla_scope?.procurement !== false,
          };
          setSlaScope(scope);
          setInitialSlaScope(scope);
          setPriority(dv.priority || 'normal');
        } else {
          if (!orderId) return;
          const [t, tpl, us] = await Promise.all([
            api.get(`/production-timeline/orders/${orderId}`),
            api.get('/production-timeline/sla-template', { params: { company_id: companyId } }),
            api.get('/production-timeline/users', { params: { company_id: companyId } }),
          ]);
          if (cancelled) return;
          setTimeline(t.data);
          setTemplate(tpl.data || { stages: [] });
          setNotifyUsers(us.data || []);
          setSlaOverrides({ ...(t.data?.sla_per_stage || {}) });
          setNotifyByStage({ ...(t.data?.delay_notify_by_stage || {}) });
          setDelayNotifyUserIds([...(t.data?.delay_notify_user_ids || [])]);
          setOrderRaisedNotify([...(t.data?.order_raised_notify_user_ids || [])]);
          setProcurementConfig({ ...(t.data?.procurement_config || {}) });
          const scope = {
            timeline: t.data?.sla_scope?.timeline !== false,
            procurement: t.data?.sla_scope?.procurement !== false,
          };
          setSlaScope(scope);
          setInitialSlaScope(scope);
          setPriority(t.data?.priority || 'normal');
        }
      } catch {
        toast.error(isDraft ? 'Failed to load SLA template' : 'Failed to load SLA config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, orderId, companyId, isDraft, jewelleryType]);

  const stageEntries = timeline?.stage_history || [];
  const completedKinds = new Set(stageEntries.filter((s) => s.status === 'completed').map((s) => s.kind));
  const procSummary = timeline?.procurement || {};

  const effectiveDays = (kind) => {
    const override = slaOverrides?.[kind];
    if (override !== undefined && override !== null && override !== '') return Number(override);
    const t = (template.stages || []).find((s) => s.kind === kind);
    return t ? Number(t.sla_days || 0) : 0;
  };

  const setSlaDays = (kind, val) => {
    if (completedKinds.has(kind)) return;
    setSlaOverrides((prev) => {
      const next = { ...prev };
      if (val === '' || val === null || val === undefined) delete next[kind];
      else next[kind] = Number(val);
      return next;
    });
  };

  const setStageNotify = (kind, uids) => {
    if (completedKinds.has(kind)) return;
    setNotifyByStage((prev) => {
      const next = { ...prev };
      if (!uids || uids.length === 0) delete next[kind];
      else next[kind] = uids;
      return next;
    });
  };

  const setProcurementSla = (kind, val) => {
    setProcurementConfig((prev) => {
      const next = { ...(prev || {}) };
      const cur = next[kind] || {};
      const numeric = val === '' || val === null || val === undefined ? 0 : Number(val);
      next[kind] = { ...cur, sla_days: Math.max(numeric, 0) };
      return next;
    });
  };

  const setProcurementNotify = (kind, uids) => {
    setProcurementConfig((prev) => {
      const next = { ...(prev || {}) };
      const cur = next[kind] || {};
      next[kind] = { ...cur, notify_user_ids: uids || [] };
      return next;
    });
  };

  const save = async () => {
    if (isDraft) {
      // Wave 3.85 — Draft mode: propagate values to caller; no HTTP call here.
      const payload = {
        sla_per_stage: slaOverrides,
        delay_notify_by_stage: notifyByStage,
        delay_notify_user_ids: delayNotifyUserIds,
        procurement_config: procurementConfig,
        sla_scope: slaScope,
        priority,
        order_raised_notify_user_ids: orderRaisedNotify,
      };
      if (onDraftSave) onDraftSave(payload);
      toast.success('SLA saved for this order draft');
      onOpenChange(false);
      if (onSaved) onSaved();
      return;
    }
    if (!orderId) return;
    setSaving(true);
    try {
      const payload = {
        sla_per_stage: slaOverrides,
        delay_notify_by_stage: notifyByStage,
        delay_notify_user_ids: delayNotifyUserIds,
        procurement_config: procurementConfig,
        priority,
        order_raised_notify_user_ids: orderRaisedNotify,
      };
      await api.put(`/orders/${orderId}/sla-config`, payload);
      // Wave 3.83 — Persist SLA scope changes via the dedicated toggle
      // endpoint (CA/SA only). Silent no-op if the user cannot toggle.
      const scopeChanged =
        initialSlaScope.timeline !== slaScope.timeline ||
        initialSlaScope.procurement !== slaScope.procurement;
      if (scopeChanged && canToggleTracking) {
        try {
          await api.post(`/orders/${orderId}/sla-tracking`, { scope: slaScope });
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Failed to update SLA tracking scope');
        }
      }
      toast.success('SLA updated');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update SLA');
    } finally {
      setSaving(false);
    }
  };

  // Wave 3.85 — In draft mode CA/SA-only guard is not required (the order
  // doesn't exist yet, so anyone creating an order can pick its scope).
  const canToggleScope = isDraft ? true : canToggleTracking;

  const dialogTitle = isDraft
    ? `Configure Production SLA${jewelleryType ? ` · ${jewelleryType}` : ''}`
    : `Edit Production SLA · ${order?.order_no || ''}`;

  const dialogDescription = isDraft
    ? 'Adjust SLA (days) and delay-alert recipients per stage. Defaults are seeded from the SLA template for the selected jewellery type.'
    : 'Adjust SLA (days) and delay-alert recipients for any stage that is not yet completed. Completed stages are locked to preserve the audit trail.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={isDraft ? 'draft-order-sla-dialog' : 'edit-order-sla-dialog'}>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center text-sm text-slate-500 py-8">Loading…</div>
        ) : (
          <div className="space-y-4">
            {/* Wave 3.83 — SLA tracking scope */}
            <div className="rounded-md border border-[var(--divider)] bg-white px-3 py-2">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[12px] font-medium text-[var(--ink-navy)]">SLA Tracking</div>
                  <div className="text-[10px] text-slate-500">
                    Turn off to suppress SLA delay alerts for this order. Tracks and timings still update for visibility.
                    {canToggleScope ? '' : ' Only Company Admins can change these.'}
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <label className={`flex items-center gap-2 text-[12px] ${canToggleScope ? 'text-slate-700 cursor-pointer' : 'text-slate-400 cursor-not-allowed'}`} data-testid="edit-sla-scope-timeline">
                    <input
                      type="checkbox"
                      checked={!!slaScope.timeline}
                      disabled={!canToggleScope}
                      onChange={(e) => setSlaScope((p) => ({ ...p, timeline: e.target.checked }))}
                      className="accent-[var(--ink-navy)]"
                    />
                    Track Production Timeline SLA
                  </label>
                  <label className={`flex items-center gap-2 text-[12px] ${canToggleScope ? 'text-slate-700 cursor-pointer' : 'text-slate-400 cursor-not-allowed'}`} data-testid="edit-sla-scope-procurement">
                    <input
                      type="checkbox"
                      checked={!!slaScope.procurement}
                      disabled={!canToggleScope}
                      onChange={(e) => setSlaScope((p) => ({ ...p, procurement: e.target.checked }))}
                      className="accent-[var(--ink-navy)]"
                    />
                    Track Material Procurement SLA
                  </label>
                </div>
              </div>
            </div>

            {/* Priority */}
            <div className="flex items-center gap-3">
              <Label className="w-32 text-[12px]">Priority</Label>
              <div className="flex gap-1.5">
                {['normal', 'high', 'urgent'].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`px-2.5 py-1 text-[11px] rounded-md border capitalize ${priority === p ? 'bg-[var(--ink-navy)] text-white border-[var(--ink-navy)]' : 'bg-white text-slate-700 border-[var(--divider)]'}`}
                    data-testid={`edit-sla-priority-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Wave 3.99ah — Global "Order Raised" notify list (notify-only). */}
            <div className="rounded-md border border-[var(--gold-accent)]/40 bg-[var(--paper-warm)] px-3 py-2" data-testid="edit-sla-order-raised-row">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[12px] font-medium text-[var(--ink-navy)]">Order Raised</div>
                  <div className="text-[10px] text-slate-500">
                    {isDraft
                      ? 'Pinged the moment this order is created. Instant alert only (no SLA).'
                      : 'These users were pinged when the order was raised. Editing here updates the record for future reference (the raise alert only fires once, at creation).'}
                  </div>
                </div>
                <div className="min-w-[200px]">
                  <StageNotifyPicker
                    users={notifyUsers}
                    value={orderRaisedNotify}
                    onChange={(uids) => setOrderRaisedNotify(uids || [])}
                    testId="edit-sla-order-raised-notify"
                    ariaLabel="Order raised notify recipients"
                  />
                </div>
              </div>
            </div>

            {/* Per-stage rows (with material rows replacing Fulfilling Requirements) */}
            <div className="border border-[var(--divider)] rounded-md overflow-hidden bg-white">
              <div className="grid grid-cols-[130px_1fr_100px_100px_200px] gap-2 px-3 py-1.5 bg-slate-50 border-b border-[var(--divider)] text-[10px] uppercase tracking-wider text-slate-500">
                <div>Status</div>
                <div>Stage</div>
                <div className="text-right">Time in Stage</div>
                <div className="text-right">SLA (days)</div>
                <div>Notify on Delay</div>
              </div>
              <div className="divide-y divide-[var(--divider)]">
                {(template.stages || []).flatMap((s) => {
                  // Wave 3.82 — Replace the "fulfilling_requirements" row with
                  // four separate material procurement rows.
                  if (s.kind === 'fulfilling_requirements') {
                    return PROCUREMENT_MATERIALS.map((m) => {
                      const cur = (procurementConfig || {})[m.kind] || {};
                      const days = cur.sla_days ?? m.defaultSla;
                      const track = procSummary[m.kind] || {};
                      const isActive = track.status === 'active';
                      const isCompleted = track.status === 'completed';
                      const notStarted = !isActive && !isCompleted;
                      const isDelayed = !!track.is_delayed;
                      const isSevere = !!track.is_severe;
                      // Wave 3.87 — When Raw Material checklist step is
                      // ticked, side materials are force-completed via a
                      // stored override. Show a clearer label.
                      const overrideReason = track.override_reason || '';
                      const forcedComplete = isCompleted && overrideReason === 'raw_material_ticked';

                      const statusLabel = isCompleted
                        ? (forcedComplete ? 'Completed · via Raw Material' : 'Completed')
                        : isActive ? (isSevere ? 'Delayed 2×' : (isDelayed ? 'Delayed' : 'Active now'))
                        : 'Not started';
                      const statusIcon = isCompleted ? <CheckCircle2 size={12} className="text-emerald-600" />
                        : isActive ? <Package size={12} className={isSevere ? 'text-rose-600' : (isDelayed ? 'text-amber-600' : 'text-[var(--gold-accent)]')} />
                        : <Lock size={12} className="text-slate-300" />;
                      const bg = isSevere ? 'bg-rose-50/50'
                        : isDelayed ? 'bg-amber-50/50'
                        : isActive ? 'bg-[var(--paper-warm)]/60'
                        : '';
                      const statusColor = isCompleted ? 'text-emerald-700'
                        : isSevere ? 'text-rose-700'
                        : isDelayed ? 'text-amber-700'
                        : isActive ? 'text-[var(--gold-accent)]'
                        : 'text-slate-500';

                      // Requirement count chip
                      const rc = track.requirement_counts || {};
                      const showReqChip = (rc.total || 0) > 0;
                      const timeStr = track.hours ? formatHours(track.hours) : '—';

                      return (
                        <div key={`proc-${m.kind}`} className={`grid grid-cols-[130px_1fr_100px_100px_200px] gap-2 px-3 py-2 items-center ${bg}`}>
                          <div className="inline-flex items-center gap-1.5 text-[11px]">
                            {statusIcon}
                            <span className={statusColor}>{statusLabel}</span>
                          </div>
                          <div className="text-[12px] text-[var(--ink-navy)] truncate flex items-center gap-2 min-w-0">
                            <span className="truncate">{m.label}</span>
                            {showReqChip && (
                              <span
                                className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800"
                                title={`${rc.completed || 0} of ${rc.total || 0} requirements fulfilled`}
                                data-testid={`edit-sla-proc-progress-${m.kind}`}
                              >
                                {rc.completed || 0}/{rc.total || 0} reqs
                              </span>
                            )}
                          </div>
                          <div className="text-right text-[11px] font-mono tabular-nums text-slate-600">
                            {timeStr}
                          </div>
                          <div className="flex items-center gap-1 justify-end">
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={days}
                              onChange={(e) => setProcurementSla(m.kind, e.target.value)}
                              disabled={isCompleted}
                              className="w-16 h-8 text-right text-[12px] font-mono tabular-nums disabled:bg-slate-50 disabled:text-slate-400"
                              data-testid={`edit-sla-proc-days-${m.kind}`}
                            />
                            <span className="text-[10px] text-slate-500 w-4">d</span>
                          </div>
                          <div className="min-w-0">
                            <StageNotifyPicker
                              users={notifyUsers}
                              value={cur.notify_user_ids || []}
                              onChange={(uids) => setProcurementNotify(m.kind, uids)}
                              disabled={isCompleted}
                              testId={`edit-sla-proc-notify-${m.kind}`}
                              ariaLabel={`Notify on ${m.label} delay`}
                            />
                          </div>
                        </div>
                      );
                    });
                  }

                  // Regular stage row (unchanged from Wave 3.78)
                  const seg = stageEntries.find((h) => h.kind === s.kind);
                  const status = seg?.status || 'not_started';
                  const isCompleted = status === 'completed';
                  const isActive = status === 'active';
                  const isBlocked = status === 'blocked';
                  const blockedByLabel = seg?.blocked_by_label || '';
                  const statusLabel = isCompleted ? 'Completed'
                    : isActive ? 'Active now'
                    : isBlocked ? `Waiting · ${blockedByLabel}`
                    : 'Not started';
                  const statusIcon = isCompleted ? <CheckCircle2 size={12} className="text-emerald-600" />
                    : isActive ? <Timer size={12} className="text-[var(--gold-accent)]" />
                    : isBlocked ? <PauseCircle size={12} className="text-stone-500" />
                    : <Lock size={12} className="text-slate-300" />;
                  const bg = isActive ? 'bg-[var(--paper-warm)]/60'
                    : isBlocked ? 'bg-stone-50'
                    : '';
                  const statusColor = isCompleted ? 'text-emerald-700'
                    : isActive ? 'text-[var(--gold-accent)]'
                    : isBlocked ? 'text-stone-600'
                    : 'text-slate-500';

                  // Requirement progress chip on Setting when blocked-by-fulfilling
                  const progress = timeline?.requirements_progress;
                  const showProgressChip = progress && progress.total > 0 && (
                    (s.kind === 'setting' && isBlocked && seg?.blocked_by === 'fulfilling_requirements')
                  );

                  return [(
                    <div key={s.kind} className={`grid grid-cols-[130px_1fr_100px_100px_200px] gap-2 px-3 py-2 items-center ${bg}`}>
                      <div className="inline-flex items-center gap-1.5 text-[11px]" title={isBlocked ? `Waiting on ${blockedByLabel} to complete first` : undefined}>
                        {statusIcon}
                        <span className={statusColor}>{statusLabel}</span>
                      </div>
                      <div className="text-[12px] text-[var(--ink-navy)] truncate flex items-center gap-2 min-w-0">
                        <span className="truncate">{slaConfigStageLabel(s.kind, s.label)}</span>
                        {showProgressChip && (
                          <span
                            className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800"
                            title={`${progress.completed} of ${progress.total} requirements fulfilled`}
                            data-testid={`edit-sla-progress-chip-${s.kind}`}
                          >
                            {progress.completed}/{progress.total} reqs
                          </span>
                        )}
                      </div>
                      <div className="text-right text-[11px] font-mono tabular-nums text-slate-600">
                        {seg?.hours ? formatHours(seg.hours) : '—'}
                      </div>
                      <div className="flex items-center gap-1 justify-end">
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          value={effectiveDays(s.kind)}
                          onChange={(e) => setSlaDays(s.kind, e.target.value)}
                          disabled={isCompleted}
                          className="w-16 h-8 text-right text-[12px] font-mono tabular-nums disabled:bg-slate-50 disabled:text-slate-400"
                          data-testid={`edit-sla-days-${s.kind}`}
                        />
                        <span className="text-[10px] text-slate-500 w-4">d</span>
                      </div>
                      <div className="min-w-0">
                        <StageNotifyPicker
                          users={notifyUsers}
                          value={notifyByStage[s.kind] || []}
                          onChange={(uids) => setStageNotify(s.kind, uids)}
                          disabled={isCompleted}
                          testId={`edit-sla-notify-${s.kind}`}
                          ariaLabel={`Notify on ${slaConfigStageLabel(s.kind, s.label)} delay`}
                        />
                      </div>
                    </div>
                  )];
                })}
              </div>
            </div>

            {/* Wave 3.83 — Fallback recipients row */}
            <div className="rounded-md border border-[var(--divider)] bg-white px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[12px] font-medium text-[var(--ink-navy)]">Fallback Recipients</div>
                  <div className="text-[10px] text-slate-500">Pinged when a material or stage has no specific recipients configured.</div>
                </div>
                <div className="min-w-[200px]">
                  <StageNotifyPicker
                    users={notifyUsers}
                    value={delayNotifyUserIds}
                    onChange={(uids) => setDelayNotifyUserIds(uids)}
                    testId="edit-sla-fallback-notify"
                    ariaLabel="Fallback delay-notify recipients"
                  />
                </div>
              </div>
            </div>

            <div className="text-[10px] text-slate-500">
              {isDraft
                ? 'These SLA and notify settings are applied to this order the moment it\'s created. You can revisit them anytime from the Order Detail page.'
                : 'Locked stages have already been completed; their SLA and recipient list are preserved for audit. Blocked stages are waiting on prerequisites (e.g. Setting waits for all requirements to be fulfilled, QC waits for the piece to be manufactured) — SLA/recipients can still be edited here.'}
              {' '}Each material track (Gold / Diamonds / Colour Stones / Pearls) has its own SLA and notify list;
              if a material has no recipient we ping the Fallback Recipients list.
            </div>

            {/* Recipients recap */}
            {(() => {
              const stageIds = Object.values(notifyByStage || {}).flat();
              const procIds  = Object.values(procurementConfig || {}).flatMap((c) => c?.notify_user_ids || []);
              const allIds = Array.from(new Set([...stageIds, ...procIds, ...(delayNotifyUserIds || [])]));
              const allUsers = notifyUsers.filter((u) => allIds.includes(u.id));
              if (allUsers.length === 0) {
                return (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="edit-sla-recipients-empty-note">
                    No delay-notification recipients configured. If any not-yet-completed stage or procurement track runs late, no one will be pinged.
                  </div>
                );
              }
              return (
                <div className="text-[11px] text-slate-600 bg-slate-50 border border-[var(--divider)] rounded-md px-3 py-2" data-testid="edit-sla-recipients-recap">
                  <span className="font-medium text-[var(--ink-navy)]">Will be pinged on delay:</span>{' '}
                  {allUsers.slice(0, 6).map((u) => (
                    <span key={u.id} className="inline-block mr-1.5">
                      {u.name || u.email}
                    </span>
                  ))}
                  {allUsers.length > 6 ? ` +${allUsers.length - 6} more` : ''}
                </div>
              );
            })()}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid={isDraft ? 'draft-order-sla-save-button' : 'edit-order-sla-save-button'}>
            {saving ? 'Saving…' : (isDraft ? 'Apply SLA' : 'Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
